/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { getOpOptions } from 'components/SmartSearch/SearchUtils';
import { helpTextSelector } from 'designer/state';
import { memo, useEffect, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { useTranslate } from '../../../../../utils/lang';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { MultiSelectOperatorProp } from 'componentregistry';
import { useLatest } from '@cloudio-saas/statex';
import useMounted from 'hooks/useMounted';

function MultiSelectOperatorProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<MultiSelectOperatorProp, string>) {
  const { t } = useTranslate();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [options, setOptions] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);
  const [state, setState] = useState([]);
  const mounted = useMounted();
  const latestValue = useLatest(value);

  useEffect(() => {
    if (def.refreshCallback) {
      def.refreshCallback(() => {
        setOptions([]);
        setState([]);
      });
    }
  }, [def]);

  useEffect(() => {
    const page = platform.getPage(appUid, pageId);
    const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
    def
      .getDataType(itemWithDefaults, {
        appUid,
        itemId: activeItemId,
        pageId,
        mgr,
        platform,
      })
      .then((_dataType) => {
        if (mounted.current) {
          const allOperators = getOpOptions(_dataType, t, false);
          const currentValues = latestValue.current?.split(';') || [];
          const hasInvalidValue = currentValues.some(
            (val) => !allOperators.some((op) => op.value === val),
          );

          if (hasInvalidValue) {
            platform.forceUpdater(() => {
              onChange(property, undefined);
            });
          }
          setOptions(allOperators);
        }
      });
  }, [
    activeItemId,
    appUid,
    def,
    mgr,
    mounted,
    pageId,
    platform,
    t,
    state,
    onChange,
    property,
  ]);

  const handleChange = (
    _value: string | undefined,
    _option: Option | null,
    selectedOptions?: Option[] | null,
  ) => {
    if (selectedOptions?.length) {
      const valueStr = selectedOptions.map((o) => o.value).join(';');
      onChange(property, valueStr);
    } else {
      onChange(property, undefined);
    }
  };
  if (options.length === 0) return null;

  return (
    <VirtualizedSelectInput
      disabled={isViewOnlyUser}
      data-testid={property}
      variant="standard"
      fullWidth
      value={value}
      options={options}
      onChange={handleChange}
      label={def.label}
      placeholder="Choose operator(s)"
      required={false}
      helpText={helpText}
      multi
      delimiter=";"
    />
  );
}

export default memo(MultiSelectOperatorProperty);
